import { useEffect, useMemo, useRef, useState, memo } from 'react'
import { Box, Stack, Card, CardContent, CardMedia, Typography, IconButton, CardHeader } from '@mui/material'
import { keyframes } from '@mui/system'
import { useIsMobile } from '@/hooks/Responsive'
import { useLang } from '@/i18n/useLang'
import { visuallyHidden } from '@mui/utils'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import PauseIcon from '@mui/icons-material/Pause'
import NorthEastIcon from '@mui/icons-material/NorthEast';

import background_image from '@assets/images/main/main_virtuallab_background.png'
import intro_image from '@assets/images/main/main_vituallab_intro.png'

import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, Pagination, Autoplay, A11y, Keyboard, EffectFade } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/effect-fade'

import { getMainContentList, getNotice } from '@/api/main'

const arrow = keyframes` 
  0%, 60% {
    transform: translate(0, 0);
    opacity: 1;
  }
  20% {
    transform: translate(100%, -100%);
    opacity: 1;
  }
  21% {
    opacity: 0;
  }
  50% {
    opacity: 0;
    transform: translate(-100%, 100%);
  }
  51% {
    opacity: 1;
    transform: translate(-50%, 50%);
  }
`

function normalizeContentList(raw) {
  const list = raw?.data ?? raw ?? []
  if (!Array.isArray(list)) return []

  return list.map((item, idx) => {
    const vlabContentId =
      item?.vlabContentId ?? ''

    const title =
      item?.vlabContentTitle ?? ''

    const badge =
      item?.vlabContentDivision ?? ''

    const desc =
      item?.vlabContentSummary ?? ''

    // ===========================
    // 기존 로직(상대경로였을 때)
    // const img = item?.vlabThumbImg ?? ''
    //
    // 현재 로직: API에서 이미 "풀 URL"로 내려오므로 그대로 사용
    const img =
      (item?.vlabThumbImg && String(item.vlabThumbImg).trim() !== '')
        ? String(item.vlabThumbImg).trim()
        : ''
    // ===========================

    return {
      id: item?.id ?? idx + 1,      // Swiper key 안정용
      vlabContentId,               // 상세 이동용
      title,
      badge,
      desc,
      img,
    }
  })
}

function normalizeNoticeList(raw) {
  const list =
    raw?.data?.content ??
    raw?.data ??
    raw ??
    []

  if (!Array.isArray(list)) return []

  return list.map((item, idx) => {
    const id = item?.vlabNoticeId ?? idx + 1
    const title = item?.vlabNoticeTitle ?? ''
    const createdAt = item?.vlabNoticeWriteDt ?? ''
    const category = item?.vlabNoticeDivision ?? ''
    const attachments = item?.vlabNoticeAddFile ?? null

    return { id, title, createdAt, category, attachments }
  })
}

const Slider = memo(({ slides = [] }) => {
  const { isMobile } = useIsMobile()
  const lang = useLang()

  const swiperRef = useRef(null)
  const [isTransitioning, setIsTransitioning] = useState(false)

  const [activeIndex, setActiveIndex] = useState(0)
  const [isPlaying, setIsPlaying] = useState(true)

  const safeSlides = useMemo(() => (Array.isArray(slides) ? slides : []), [slides])

  useEffect(() => {
    setActiveIndex(0)
  }, [safeSlides.length])

  const togglePlay = () => {
    if (!swiperRef.current) return

    if (isPlaying) swiperRef.current.autoplay.stop()
    else swiperRef.current.autoplay.start()

    setIsPlaying(!isPlaying)
  }

  const activeVlabContentId = safeSlides?.[activeIndex]?.vlabContentId

  return (
    <Box sx={{ position: 'relative', width: '100%', height: isMobile ? '243px' : '100%', borderRadius: 4, overflow: 'hidden' }}>
      {/* Swiper */}
      <Swiper
        modules={[Navigation, Pagination, Autoplay, A11y, Keyboard, EffectFade]}
        effect="fade"
        fadeEffect={{ crossFade: true }}
        speed={1200}
        loop={safeSlides.length > 1}
        keyboard={{ enabled: true }}
        autoplay={isPlaying ? { delay: 3000, disableOnInteraction: false } : false}
        onSwiper={(sw) => (swiperRef.current = sw)}

        onSlideChangeTransitionStart={() => {
          setIsTransitioning(true)
        }}

        onSlideChangeTransitionEnd={() => {
          setIsTransitioning(false)
        }}

        onSlideChange={(sw) => setActiveIndex(sw.realIndex)}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          zIndex: 1,
        }}
      >
        {safeSlides.length > 0 ? (
          safeSlides.map((slide) => (
            <SwiperSlide key={slide.id}>
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,

                  // ===========================
                  // 기존 로직(상대경로일 때)
                  // backgroundImage: slide.img ? `url(https://www.openvlab.kr/${slide.img})` : 'none',
                  //
                  // 현재 로직: slide.img 자체가 "풀 URL"이므로 그대로 사용
                  backgroundImage: slide.img ? `url(${slide.img})` : 'none',
                  // ===========================

                  backgroundSize: isMobile ? 'cover' : 'auto 100%',
                  backgroundPosition: 'right center',
                  backgroundRepeat: 'no-repeat',
                  bgcolor: slide.img ? 'transparent' : 'grey.900',
                }}
              />
            </SwiperSlide>
          ))
        ) : (
          <SwiperSlide key="empty">
            <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'grey.900' }} />
          </SwiperSlide>
        )}
      </Swiper>

      {/* 고정 오버레이 */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 2,
          pointerEvents: 'none',
          background: isMobile
            ? 'rgba(0,0,0,0.3)'
            : 'linear-gradient(to left, rgba(0,0,0,0) 45%, rgba(0,0,0,1) 60%)',
        }}
      />

      {/* 텍스트 전체 레이어 */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          py: isMobile ? 4 : 5,
          px: isMobile ? 3 : 5,
          zIndex: 3
        }}
      >
        {/* 접근성 용 */}
        <Typography
          component="p"
          role="status"
          aria-live="polite"
          sx={visuallyHidden}
        >
          {isPlaying
            ? `자동재생 중, ${activeIndex + 1}번 슬라이드`
            : `자동재생 정지, ${activeIndex + 1}번 슬라이드`}
        </Typography>

        {/* 제목 */}
        <Typography fontWeight={700} sx={{ mb: 0.5, color: 'text.contrastText' }}>
          XR콘텐츠 체험신청
        </Typography>

        {/* 슬라이드별 텍스트 */}
        <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
          {(safeSlides.length > 0 ? safeSlides : [{ id: 'empty', title: '콘텐츠를 불러오는 중입니다.', desc: '', img: '' }]).map(
            (slide, index) => (
              <Box key={slide.id}>
                <Stack
                  key={index}
                  aria-hidden={activeIndex !== index}
                  sx={{
                    opacity: activeIndex === index ? 1 : 0,
                    transition: 'opacity .8s ease .3s',
                    position: 'absolute',
                    inset: 0,
                    color: 'text.contrastText',
                  }}
                >
                  <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight={700}>
                    {slide.title}
                  </Typography>
                  <Typography
                    sx={{
                      maxWidth: '406px',
                      mt: 1,
                      display: isMobile ? 'none' : '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {slide.desc}
                  </Typography>
                </Stack>

                {/* 바로가기 */}
                {slide.id !== 'empty' && (
                  <IconButton
                    component="a"
                    href={activeVlabContentId ? `https://www.openvlab.kr/lab/apply/apply/${activeVlabContentId}` : undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${slide.title} 상세보기 (새 창)`}
                    sx={{
                      position: 'absolute',
                      right: '0',
                      top: '-32px',
                      width: '40px',
                      height: '40px',
                      bgcolor: 'background.paper',
                      color: 'grey.dark',
                      overflow: 'hidden',
                      '&:hover': {
                        bgcolor: 'background.paper',
                        '& .arrow-icon': {
                          animation: `${arrow} 0.5s ease-in-out`,
                        },
                      },
                    }}
                  >
                    <span id={`slide-link-${slide.id}`} style={visuallyHidden}>
                      {slide.title} 상세보기 (새 창)
                    </span>
                    <NorthEastIcon className="arrow-icon" />
                  </IconButton>
                )}
              </Box>
            )
          )}
        </Box>

        {/* 하단 컨트롤 */}
        <Stack direction="row" spacing={1} sx={{ position: 'absolute', left: isMobile ? '24px' : '40px', bottom: isMobile ? '24px' : '40px', }}>
          {/* Prev/Next + Indicator */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ bgcolor: 'grey.900', borderRadius: 7, px: 1, color: 'divider' }}>
            <IconButton
              onClick={() => {
                if (isTransitioning) return
                swiperRef.current?.slidePrev()
              }}
              disabled={isTransitioning}
              aria-label="이전 슬라이드"
              sx={{
                p: 0,
                color: 'divider',
                '&.Mui-disabled': {
                  color: 'divider',
                  opacity: 0.6,
                  cursor: 'not-allowed',
                },
              }}
            >
              <ChevronLeftIcon sx={{ fontSize: '20px' }} />
            </IconButton>

            <Typography variant="body3" fontWeight={700}>
              {safeSlides.length > 0 ? activeIndex + 1 : 0}
            </Typography>

            <Box sx={{ width: '1px', height: '10px', bgcolor: 'divider' }} />

            <Typography variant="body3">
              {safeSlides.length}
            </Typography>

            <IconButton
              onClick={() => {
                if (isTransitioning) return
                swiperRef.current?.slideNext()
              }}
              disabled={isTransitioning}
              aria-label="다음 슬라이드"
              sx={{
                p: 0,
                color: 'divider',
                '&.Mui-disabled': {
                  color: 'divider',
                  opacity: 0.6,
                  cursor: 'not-allowed',
                },
              }}
            >
              <ChevronRightIcon sx={{ fontSize: '20px' }} />
            </IconButton>
          </Stack>

          {/* Play/Pause */}
          <Box
            sx={{
              bgcolor: 'grey.900',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              display: 'inline-flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <IconButton
              onClick={togglePlay}
              aria-label={isPlaying ? '자동재생 일시정지' : '자동재생 재생'}
              sx={{ color: 'white', p: 0 }}
            >
              {isPlaying ? (
                <PauseIcon sx={{ fontSize: '18px' }} />
              ) : (
                <PlayArrowIcon sx={{ fontSize: '18px' }} />
              )}
            </IconButton>
          </Box>
        </Stack>
      </Box>
    </Box>
  )
})

export default function VirtualLabSection() {
  const { isMobile } = useIsMobile()
  const lang = useLang()

  const [slides, setSlides] = useState([])
  const [latestNotices, setLatestNotices] = useState([])

  useEffect(() => {
    const fetchMain = async () => {
      try {
        const [contentRes, noticeRes] = await Promise.all([
          getMainContentList(),
          getNotice({
            page: 1,
            pageSize: 5,
            keyword: '',
          }),
        ])

        const mappedSlides = normalizeContentList(contentRes)
        const mappedNotices = normalizeNoticeList(noticeRes)

        setSlides(mappedSlides)
        setLatestNotices(mappedNotices.slice(0, 3))
      } catch (e) {
        console.error('가상체험관 메인 데이터 조회 실패', e)
        setSlides([])
        setLatestNotices([])
      }
    }

    fetchMain()
  }, [])

  return (
    <Box
      sx={{
        mx: 'auto',
        py: isMobile ? 10 : 15,
        width: '100%',
        minHeight: isMobile ? 'initial' : 'calc(100vh - 120px)',
        px: 2,
        backgroundImage: `url(${background_image})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {/* 대제목 */}
      <Stack direction={isMobile ? 'column' : 'row'} alignItems={isMobile ? 'flex-start' : 'flex-end'} justifyContent={isMobile ? 'flex-start' : 'space-between'} spacing={2} sx={{ maxWidth: '1440px', px: 2, mx: 'auto', mb: 7, color: 'text.contrastText' }}>
        <Box>
          <Typography component="h3" variant={isMobile ? 'body1' : 'subtitle3'} fontWeight={700} sx={{ color: 'primary.main', mb: 2 }}>
            가상실험실습체험관
          </Typography>
          <Typography component="p" fontWeight={600} variant={isMobile ? 'h6' : 'h4'}>
            눈으로 보고, 몸으로 배우는<br />
            XR 학습의 현장
          </Typography>
        </Box>
        <Stack
          component="a"
          href="https://www.openvlab.kr/lab"
          target="_blank"
          title="체험관 소개 바로가기(새 창)"
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            pb: 1.5,
            textDecoration: 'none',
            color: 'inherit',
            display: 'inline-flex',
            '&:hover .arrow-icon': {
              animation: `${arrow} 0.5s ease-in-out`,
            },
          }}
        >
          <Typography fontWeight={600}>
            자세히 보기
          </Typography>

          <IconButton
            aria-hidden="true"
            sx={{
              width: isMobile ? '32px' : '40px',
              height: isMobile ? '32px' : '40px',
              border: '1px solid',
              borderColor: 'text.contrastText',
              color: 'text.contrastText',
              pointerEvents: 'none',
              overflow: 'hidden',
            }}
          >
            <NorthEastIcon className="arrow-icon" sx={{ fontSize: '20px' }} />
          </IconButton>
        </Stack>
      </Stack>
      <Stack direction={isMobile ? 'column' : 'row'}
        sx={{
          width: '100%',
          maxWidth: '1400px',
          mx: 'auto',
          gap: 3
        }}
      >
        {/* 체험관 소개 */}
        <Card
          sx={{
            p: isMobile ? 3 : 5,
            width: isMobile ? 'auto' : '480px',
            backdropFilter: 'blur(10px)',
            boxShadow: '0px 0px 30px 0px #0B010025',
            color: 'white',
            borderRadius: 4,
            bgcolor: '#FFFFFF15',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <CardHeader
            titleTypographyProps={{
              component: 'h3',
              variant: isMobile ? 'subtitle1' : 'h6',
            }}
            title="체험관 소개"
            action={
              <IconButton
                component="a"
                href="https://www.openvlab.kr/lab/about"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  width: isMobile ? '32px' : '40px',
                  height: isMobile ? '32px' : '40px',
                  bgcolor: 'background.paper',
                  color: 'grey.dark',
                  overflow: 'hidden',
                  '&:hover': {
                    bgcolor: 'background.paper',
                    '& .arrow-icon': {
                      animation: `${arrow} 0.5s ease-in-out`,
                    },
                  },
                }}
              >
                <span style={visuallyHidden}>
                  체험관 소개 바로가기 (새 창)
                </span>
                <NorthEastIcon className="arrow-icon" sx={{ fontSize: isMobile ? '20px' : '24px'}} />
              </IconButton>
            }
            sx={{ p: 0, mb: 3 }}
          />
          <CardContent sx={{ p: 0 }}>
            <Typography
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: lang === 'ko' ? 4 : 6,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              한국방송통신대학교의 가상실험실습체험관은 디지털 기술에 익숙하지 않은 분들도 부담 없이 참여할 수 있도록, 실용적이고 기본적인 커리큘럼을 가진 콘텐츠들로 구성되어 있습니다.
            </Typography>
          </CardContent>
          {lang === 'ko' && (
            <CardMedia
              component="img"
              image={intro_image}
              alt="VR 체험존"
              sx={{ width: '100%', borderRadius: 2, mt: isMobile ? 5 : 'auto' }}
            />
          )}
        </Card>

        <Stack flex="1" spacing={3} sx={{ position: 'relative'}}>
          {/* XR콘텐츠 */}
          <Card
            sx={{
              width: '100%',
              height: isMobile ? 'auto' : '304px',
              position: 'relative',
              p: 0,
              borderRadius: 0,
              bgcolor: 'transparent'
            }}
          >
            <Slider slides={slides} />
          </Card>
          {/* 체험관 공지사항 */}
          {lang === 'ko' && (
            <Card
              sx={{
                p: isMobile ? 3 : 5,
                width: '100%',
                backdropFilter: 'blur(10px)',
                boxShadow: '0px 0px 30px 0px #0B010025',
                color: 'text.contrastText',
                borderRadius: 4,
                bgcolor: '#FFFFFF15',
                display: 'flex',
                alignItems: isMobile ? 'flex-start' : 'center',
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? 3 : 5,
              }}
            >
              <Typography component="h3" variant={isMobile ? 'subtitle2' : 'subtitle1'}>체험관 공지사항</Typography>
              <Stack spacing={2} sx={{ flex: 1 }}>
                {latestNotices.map((item) => (
                  <Stack key={item.id}>
                    <Stack flexDirection={isMobile ? 'column' : 'row'} alignItems={isMobile ? 'flex-start' : 'center'} justifyContent="space-between">
                      <Typography
                        component="a"
                        href={`https://www.openvlab.kr/lab/guide/notice/${item.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                      >
                        {item.title}
                      </Typography>

                      <Typography sx={{ ml: isMobile ? 0 : 3, mt: isMobile ? 1 : 0 }}>
                        {item.createdAt}
                      </Typography>
                    </Stack>
                  </Stack>
                ))}

                {latestNotices.length === 0 && <Typography sx={{ opacity: 0.9 }}>공지사항이 없습니다.</Typography>}
              </Stack>
            </Card>
          )}
        </Stack>
      </Stack>
    </Box>
  )
}
